import { NgRedux } from '@ptl/angular-redux-store';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';
import { BrandingConfigurationService } from '@ptl/brand';
import { PtlNotificationService, PtlNotificationStyle } from '@ptl/global-components';
import { StateStructure } from '@ptl/state';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, AxwayTokenFetcherService } from '@ptl/xhr';
import { ReducerUtilityService } from '../../../../shared/utility-services/reducer-utility.service';
import { SoaDashboardParams } from '../../../producer/interfaces/SOA/soa-dashboard-params.interface';
import { SoaDashboardResponse } from '../../../producer/interfaces/SOA/soa-dashboard-response.interface';
import { SendSoaRequest, SendSoaResponse, SoaMessageResponse, SoaScopeOfSalesData } from '../interfaces/soa.interface';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';

@Injectable({
  providedIn: 'root'
})
export class SoaService {

  constructor(
    private httpClient: HTTPWrapperContext,
    private ptlNotificationService: PtlNotificationService,
    private reducerUtilityService: ReducerUtilityService,
    private brandService: BrandingConfigurationService,
    protected ngRedux: NgRedux<StateStructure>,
    private commonUtilityService: CommonUtilityService,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) { }

  sendSoa(payload: SendSoaRequest): Promise<SendSoaResponse> {
    const env = this.commonUtilityService.getTestEnvironment();
    const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.EMAIL_SERVICES.SEND_EMAIL_EXACTTARGET, {}),
        headers: new HttpHeaders({
          'x-hmhs-test-backend': `wasinter${env}.highmark.com`
        }),
        POST_OR_PUT_PAYLOAD: payload,
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfig);
  }

  resendSoa(soaId: number): Promise<SoaMessageResponse> {
    const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: '/sxesvc/api/v3/electronicAppointment/resendAppointment',
        headers: new HttpHeaders({
        }),
        POST_OR_PUT_PAYLOAD: {
          soaTokenId: soaId
        },
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  submitAgentSoa(payload: SoaScopeOfSalesData): Promise<SoaMessageResponse> {
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

    const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: '/sxesvc/api/v3/electronicAppointment/agentSubmission',
        headers: new HttpHeaders({
          ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
          fromDP: loginInfo.userId
        }),
        POST_OR_PUT_PAYLOAD: payload,
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  submitPublicSoa(payload: SoaScopeOfSalesData): Promise<SoaMessageResponse> {
    const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: '/sxesvc/api/v1/soaUpdate/customerSubmission',
        headers: new HttpHeaders({
        }),
        POST_OR_PUT_PAYLOAD: payload,
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  getSoaStatus(soaTokenId): Promise<SoaMessageResponse> {
    const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: '/sxesvc/api/v1/soaUpdate/status',
        headers: new HttpHeaders({
        }),
        POST_OR_PUT_PAYLOAD: { soaTokenId },
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  getSingleAppointment(soaTokenId: string) {
    return this.getAppointments({
      emailId: null,
      soaTokenId: soaTokenId,
      pageNumber: 0,
      countPerPage: 10
    });
  }

  getAppointments(searchParams: SoaDashboardParams): Promise<SoaDashboardResponse> {
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

    const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: '/sxesvc/api/v3/electronicAppointment/retrieveAppointments',
        headers: new HttpHeaders({
          ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
          fromDP: loginInfo.userId
        }),
        POST_OR_PUT_PAYLOAD: searchParams,
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  showGenericError() {
    this.ptlNotificationService.open({
      title: 'Error',
      content: 'Something went wrong. Please try again later.',
      primaryButtonText: 'OK',
      notificationStyle: PtlNotificationStyle.DANGER
    });
  }
}